import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 32, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.21, 0.6, 0.35, 1] }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "", stagger = 0.1, ...rest }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: stagger } } }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", ...rest }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y: 28 },
      show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.6, 0.35, 1] } },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Counter = ({ value, suffix = "", duration = 1800, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, value, duration]);

  return <span ref={ref}>{decimals ? count.toFixed(decimals) : Math.round(count)}{suffix}</span>;
};

export const KineticHeadline = ({ before = "", gradient = "", after = "", className = "" }) => {
  const words = [
    ...before.split(" ").filter(Boolean).map((w) => ({ w, g: false })),
    ...gradient.split(" ").filter(Boolean).map((w) => ({ w, g: true })),
    ...after.split(" ").filter(Boolean).map((w) => ({ w, g: false })),
  ];
  return (
    <span className={className}>
      {words.map((item, i) => (
        <motion.span
          key={i}
          className={`inline-block ${item.g ? "text-gradient" : ""}`}
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, delay: i * 0.055, ease: [0.21, 0.6, 0.35, 1] }}
        >
          {item.w}{"\u00A0"}
        </motion.span>
      ))}
    </span>
  );
};

export const SpotlightCard = ({ children, className = "", ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} className={`spotlight-card ${className}`} {...rest}>
      {children}
    </div>
  );
};

export const SectionLabel = ({ children, dark = false }) => (
  <span
    className={`font-mono-brand inline-block text-[11px] font-semibold tracking-[0.2em] uppercase px-3.5 py-1 rounded-full mb-5 border ${
      dark 
        ? "bg-white/5 border-white/15 text-[#91E0F1]" 
        : "bg-[#CAF2F9]/50 border-[#91E0F1]/40 text-[#2563EB]"
    }`}
  >
    {children}
  </span>
);

export const Highlight = ({ children, type = "bg", color, delay = 0.25 }) => {
  const isUnderline = type === "underline";
  const defaultBg = "#CAF2F9";
  const bg = color && color !== "#FEF08A" ? color : defaultBg;
  
  return (
    <span className="relative inline-block px-1">
      <motion.span
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
        style={{ 
          originX: 0, 
          backgroundColor: isUnderline ? "#2563EB" : bg 
        }}
        className={`absolute inset-x-0 ${
          isUnderline 
            ? "bottom-0 h-[2px] opacity-80" 
            : "bottom-0.5 top-2 -z-10 rounded-sm opacity-60"
        }`}
      />
      <span className="relative z-10 font-semibold text-[#01193D]">{children}</span>
    </span>
  );
};

